import React, { useState, useEffect, useRef } from 'react';
import { menuCategories, galleryImages, siteImages } from '../data/mock';

const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.1, ...options });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, isInView];
};

const Menu = () => {
  const [activeCategory, setActiveCategory] = useState('tapas');
  const [heroRef, heroInView] = useInView();
  const [menuRef, menuInView] = useInView();

  const activeMenu = menuCategories.find((c) => c.id === activeCategory);

  return (
    <div className="bg-[#0f0704]">
      {/* Hero Section */}
      <section ref={heroRef} className="relative h-[50vh] min-h-[350px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 hero-parallax"
          style={{
            backgroundImage: `url('${siteImages.menuHero}')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-[#0f0704]" />
        <div className={`relative z-10 text-center px-6 ${heroInView ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <span className="text-amber-400 text-sm uppercase tracking-[0.35em] font-medium">
            Speisen & Getränke
          </span>
          <h1 className="font-brand text-4xl md:text-6xl lg:text-7xl text-white mt-4">
            Unsere Speisekarte
          </h1>
          <p className="text-white/60 mt-4 text-lg max-w-xl mx-auto">
            Werfen Sie einen Blick auf unsere Angebote
          </p>
        </div>
      </section>

      {/* Menu Section */}
      <section ref={menuRef} className="py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6">
          {/* Category Tabs */}
          <div className={`flex flex-wrap justify-center gap-2 mb-16 ${menuInView ? 'animate-fade-in-up' : 'opacity-0'}`}>
            {menuCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-6 py-2.5 rounded-full text-sm font-medium tracking-wide transition-all duration-300 ${
                  activeCategory === category.id
                    ? 'bg-amber-600 text-white shadow-lg shadow-amber-600/20'
                    : 'bg-white/[0.05] text-white/60 hover:bg-white/[0.1] hover:text-white border border-white/[0.08]'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Category Header */}
          {activeMenu && (
            <div className="text-center mb-12">
              <h2 className="font-brand text-3xl md:text-4xl text-white">
                {activeMenu.name}
              </h2>
              <p className="text-white/50 mt-2">{activeMenu.description}</p>
              <div className="w-16 h-0.5 bg-amber-400/50 mx-auto mt-4" />
            </div>
          )}

          {/* Menu Items */}
          {activeMenu && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {activeMenu.items.map((item, index) => (
                <div
                  key={item.name}
                  className="group bg-white/[0.03] border border-white/[0.06] rounded-xl p-6 hover:bg-white/[0.06] hover:border-amber-400/20 transition-all duration-400"
                  style={{ animationDelay: `${index * 80}ms` }}
                >
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <h3 className="font-playfair text-lg font-semibold text-white group-hover:text-amber-400 transition-colors duration-300">
                        {item.name}
                      </h3>
                      <p className="text-white/45 text-sm mt-1 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                    <span className="text-amber-400 font-semibold text-lg whitespace-nowrap">
                      {item.price} €
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Note */}
          <div className="mt-16 text-center">
            <p className="text-white/30 text-sm italic">
              Alle Preise in Euro inkl. MwSt. · Änderungen vorbehalten
            </p>
          </div>
        </div>
      </section>

      {/* Gallery strip */}
      <section className="py-16 bg-[#130905]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-amber-400 text-sm uppercase tracking-[0.3em] font-medium">
              Impressionen
            </span>
            <h2 className="font-brand text-3xl text-white mt-4">
              Unsere Gerichte
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.slice(0, 4).map((img, index) => (
              <div key={index} className="img-hover-zoom rounded-xl overflow-hidden">
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-48 md:h-56 object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Menu;
